import { CommonModule, Location } from '@angular/common';
import { Component, inject, OnInit, Signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../../button/button.component';
import { FileUploadComponent } from '../../file-upload/file-upload.component';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MaintenanceLogFormMetaData } from 'src/app/services/resolvers/maintenance-log-form-resolver.service';
import { LoadingService } from 'src/app/services/loading.service';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { finalize, Observable } from 'rxjs';
import {
  PartId,
  PartWithMaintenanceDetails,
} from 'src/app/models/maintenancelogs.model';
import { TitleCaseReplacePipe } from 'src/app/pipes/title-case-replace.pipe';
import { AutocompleteComponent } from '../../atuocomplete/autocomplete.component';
import { Store } from '@ngrx/store';
import {
  dispatchLoadingCompany,
  dispatchLoadingMembers,
} from 'src/app/stores/store.utils';
import { MemberType } from 'src/app/models/company.model';
import { selectAllMembers } from 'src/app/stores/company/company.selector';

@Component({
  selector: 'app-drone-maintenance-log-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FileUploadComponent,
    TitleCaseReplacePipe,
    MatCardModule,
    TranslateModule,
    MatSelectModule,
    ButtonComponent,
    AutocompleteComponent,
  ],
  templateUrl: './drone-maintenance-log-form.component.html',
  styleUrl: './drone-maintenance-log-form.component.scss',
})
export class DroneMaintenanceLogFormComponent implements OnInit {
  private location = inject(Location);
  private activatedRoute = inject(ActivatedRoute);
  private loadingService = inject(LoadingService);

  maintenanceForm!: FormGroup;

  maintenanceTypes: string[] = [];
  airCraftName!: string;
  unitNumber!: string;
  employees$: Signal<MemberType[]>;

  isUploading = false;
  partsList: PartId[] = [];
  isLoading = false;
  maintenanceLog: PartWithMaintenanceDetails | null = null;

  constructor(
    private fb: FormBuilder,
    private store: Store,
  ) {
    dispatchLoadingCompany(store);
    dispatchLoadingMembers(store);
    this.employees$ = toSignal(this.store.select(selectAllMembers), {
      initialValue: [],
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.loadingService.loading();
    this.buildForm();
    this.activatedRoute.data
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loadingService.loaded();
        }),
      )
      .subscribe((data) => {
        let formData = data['formData'] as MaintenanceLogFormMetaData;
        if (formData.maintenanceLog) {
          this.maintenanceLog = formData.maintenanceLog;
          this.patchFormValue(formData.maintenanceLog);
        }
        this.maintenanceTypes = this.maintenanceLog
          ? [
              this.maintenanceLog.typeOfMaintenance,
              ...formData?.maintenanceType,
            ]
          : formData?.maintenanceType;
        this.partsList = this.maintenanceLog
          ? [this.maintenanceLog.partId, ...formData?.partList]
          : formData?.partList;
        this.unitNumber = formData?.unitNumber;
        this.airCraftName = formData?.aircraftName;
      });
  }

  onMaintainerSelected(selected: MemberType | string) {
    if (typeof selected === 'string') {
      this.maintenanceForm.patchValue({
        maintainer: selected,
      });
    } else {
      this.maintenanceForm.patchValue({
        maintainer: selected.firstName + ' ' + selected.lastName,
      });
    }
    this.maintenanceForm.get('maintainer')?.markAsTouched();
    this.maintenanceForm.get('maintainer')?.markAsDirty();
  }
  employeeNameFormatter = (item: MemberType | null, value: string) => {
    if (!item) return this.formatName(value);
    return (
      this.formatName(item.firstName) + ' ' + this.formatName(item.lastName)
    );
  };

  // Only capitalises the first letter of each word, so names like 'deVries' keep their casing.
  private formatName(name?: string): string {
    return (name ?? '').replace(
      /(^|\s)(\S)/g,
      (_, space, char) => space + char.toUpperCase(),
    );
  }

  buildForm(): void {
    this.maintenanceForm = this.fb.group({
      part: [null, Validators.required],
      typeOfMaintenance: [null, Validators.required],
      description: ['', Validators.required],
      maintainer: ['', Validators.required],
      detailedDescription: ['', Validators.required],
      attachments: [[]], // Holds the file attachments
    });
  }

  patchFormValue(log: PartWithMaintenanceDetails): void {
    this.maintenanceForm.patchValue({
      part: log.partId,
      typeOfMaintenance: log.typeOfMaintenance,
      description: log.description,
      detailedDescription: log.detailedDescription,
      attachments: log.attachments ?? [],
      maintainer: log.maintainer,
    });
    this.maintenanceForm.get('part')?.disable();
    this.maintenanceForm.get('maintainer')?.disable();
    this.maintenanceForm.get('typeOfMaintenance')?.disable();
  }

  displayPart(part?: any): string {
    return part
      ? `${part.partId} - ${part.partType} (${part.partSubType})`
      : '';
  }

  onFileSelected(file: File): void {
    this.isUploading = false;
    if (file) {
      this.maintenanceForm.patchValue({
        attachments: [...this.maintenanceForm.get('attachments')?.value, file],
      });
      this.maintenanceForm.get('attachments')?.markAsTouched();
      this.maintenanceForm.get('attachments')?.markAsDirty();
    }
    this.isUploading = false;
  }

  removeAttachment(file: File): void {
    let fileArray = this.maintenanceForm
      .get('attachments')
      ?.value.filter((f: File) => f.name !== file.name);
    this.maintenanceForm.patchValue({ attachments: fileArray });
    this.maintenanceForm.get('attachments')?.markAsTouched();
    this.maintenanceForm.get('attachments')?.markAsDirty();
  }

  cancelForm(): void {
    this.location.back();
  }

  onSubmit(): void {
    if (this.maintenanceForm.valid) {
      console.log('Form Submitted:', this.maintenanceForm.value);
      alert('Form submitted successfully!');
    } else {
      alert('Please fill out all required fields.');
    }
  }
}
